'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../lib/cn';
import { Button } from './button';

export interface ConfirmOptions {
  /** Bold heading line. */
  title: string;
  /** Optional supporting text. Newlines are preserved. */
  description?: React.ReactNode;
  /** Label for the confirm action. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Label for the dismiss action. Defaults to "Cancel". */
  cancelLabel?: string;
  /** `danger` paints the confirm button red for destructive actions. */
  tone?: 'danger' | 'default';
  /**
   * When set, the admin must type this exact token (e.g. "DELETE") before the
   * confirm button enables. Use as a high-friction gate for irreversible
   * actions — replaces the old native `prompt('type DELETE')` pattern.
   */
  requireText?: string;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = React.createContext<ConfirmFn | null>(null);

/**
 * Promise-based confirmation. Replaces native `window.confirm()` with a styled
 * modal so every app shares one look.
 *
 * ```tsx
 * const confirm = useConfirm();
 * if (!(await confirm({ title: 'Delete "X"?', tone: 'danger', confirmLabel: 'Delete' }))) return;
 * ```
 */
export function useConfirm(): ConfirmFn {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used inside a <ConfirmProvider>');
  }
  return ctx;
}

interface DialogState extends ConfirmOptions {
  open: boolean;
}

/** Mount once near the app root so any descendant can call `useConfirm()`. */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<DialogState | null>(null);
  const resolverRef = React.useRef<((value: boolean) => void) | null>(null);

  const confirm = React.useCallback<ConfirmFn>((opts) => {
    setState({ ...opts, open: true });
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = React.useCallback((result: boolean) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setState((prev) => (prev ? { ...prev, open: false } : prev));
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog state={state} onResult={settle} />
    </ConfirmContext.Provider>
  );
}

function ConfirmDialog({
  state,
  onResult,
}: {
  state: DialogState | null;
  onResult: (result: boolean) => void;
}) {
  const open = !!state?.open;
  const requireText = state?.requireText;
  const confirmRef = React.useRef<HTMLButtonElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [typed, setTyped] = React.useState('');
  const gateOk = !requireText || typed === requireText;

  React.useEffect(() => {
    if (!open) return;
    setTyped('');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onResult(false);
      // Enter confirms only once any typed-token gate is satisfied.
      if (e.key === 'Enter' && (!requireText || inputRef.current?.value === requireText)) {
        onResult(true);
      }
    };
    window.addEventListener('keydown', onKey);
    // Focus the typed gate if present, else the confirm button.
    const id = window.setTimeout(
      () => (requireText ? inputRef.current : confirmRef.current)?.focus(),
      0,
    );
    return () => {
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(id);
    };
  }, [open, onResult, requireText]);

  if (!open || typeof document === 'undefined') return null;

  const tone = state?.tone ?? 'default';
  const danger = tone === 'danger';

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
      aria-label={state?.title}
    >
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm animate-fade-in"
        onClick={() => onResult(false)}
      />
      <div className="relative w-full max-w-sm rounded-2xl bg-white shadow-xl border border-slate-200 animate-fade-in">
        <div className="flex items-start gap-3 p-5">
          <div
            className={cn(
              'h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0',
              danger ? 'bg-red-50' : 'bg-primary-50',
            )}
          >
            <AlertTriangle className={cn('h-5 w-5', danger ? 'text-red-600' : 'text-primary-600')} />
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            <h2 className="text-base font-semibold text-slate-900">{state?.title}</h2>
            {state?.description && (
              <div className="text-sm text-slate-500 mt-1 whitespace-pre-line">{state.description}</div>
            )}
            {requireText && (
              <div className="mt-3">
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Type <span className="font-bold text-slate-900">{requireText}</span> to confirm
                </label>
                <input
                  ref={inputRef}
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  className="w-full h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 pb-5">
          <Button variant="outline" size="sm" onClick={() => onResult(false)}>
            {state?.cancelLabel ?? 'Cancel'}
          </Button>
          <Button
            ref={confirmRef}
            size="sm"
            variant={danger ? 'destructive' : 'default'}
            disabled={!gateOk}
            onClick={() => onResult(true)}
          >
            {state?.confirmLabel ?? 'Confirm'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
